export type GetAuthToken = (options?: {
  skipCache?: boolean
}) => Promise<string | null>

/**
 * Resolve the bearer token for backend API calls from the self-auth token
 * manager (in-memory access token, single-flight refresh). Browser-only —
 * returns null in a server context (server code uses
 * lib/server-session.ts:getBackendAuthTokenServer instead).
 *
 * The first argument is retained for call-site compatibility but ignored:
 * self-auth is the only path, so the token always comes from the token
 * manager. Callers may still pass their `getToken` ref without effect.
 */
export function getBackendAuthToken(
  _getToken?: GetAuthToken | null,
  options?: { skipCache?: boolean },
): Promise<string | null> {
  // tokenManager is browser-only; return null in server context.
  if (typeof window === 'undefined') {
    return Promise.resolve(null)
  }
  return import('@/lib/self-auth/token-manager').then(m =>
    // skipCache callers just saw the cached token rejected (e.g. a 401
    // retry) — force a refresh instead of re-serving the same token.
    m.tokenManager.getToken(
      options?.skipCache ? { forceRefresh: true } : undefined,
    ),
  )
}
